import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { X, ChevronLeft } from 'lucide-react';
import { CashPayoutSummaryLine, CreditSummaryLine } from './history_row';
import useWalletHistory from '../../hooks/use_wallet_history';
import { useTokenTranslate } from '../../contexts/token_translation_context';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1901;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 460px;
  height: 460px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 16px;
  box-shadow: 0 20px 60px rgba(42, 26, 110, 0.18), 0 4px 16px rgba(0,0,0,0.08);
  @media (min-width: 768px) {
    max-width: 520px;
    height: 520px;
  }
  @media (min-width: 1200px) {
    max-width: 580px;
    height: 580px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 20px 20px 16px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #2a1a6e;
  display: flex;
  align-items: center;
  margin-right: 8px;
  border-radius: 6px;
  &:hover { background: #f5f5f5; }
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #2a1a6e;
  margin: 0;
  flex: 1;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #888;
  display: flex;
  align-items: center;
  border-radius: 6px;
  &:hover { background: #f5f5f5; }
`;

const Body = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 0 20px 20px;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const SectionHeader = styled.div`
  padding: 18px 0 6px;
`;

const SectionTitle = styled.p`
  font-size: 13px;
  font-weight: 500;
  color: #777;
  margin: 0;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  font-size: 15px;
  color: #aaa;
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 0;
`;

const SkeletonRow = styled.div`
  height: 12px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  border-radius: 6px;
  width: ${props => props.skWidth || '60%'};
  animation: shimmer 1.4s infinite;
  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

const LoadMoreTrigger = styled.div`
  height: 1px;
`;

const EndOfList = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #ccc;
`;

const CASH_SECTION_TOKENS = {
  'This Week': 'web.wallet_history_modal.this_week_text',
  'Last Week': 'web.wallet_history_modal.last_week_text',
  Earlier: 'web.wallet_history_modal.earlier_text'
};

const SKELETON_ITEMS = [
  { key: 'sk-0', width: '65%' }, { key: 'sk-1', width: '40%' },
  { key: 'sk-2', width: '65%' }, { key: 'sk-3', width: '55%' },
  { key: 'sk-4', width: '65%' }, { key: 'sk-5', width: '40%' },
  { key: 'sk-6', width: '65%' }, { key: 'sk-7', width: '55%' }
];

const buildCreditSections = (credits, translate) => {
  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay());
  startOfWeek.setHours(0, 0, 0, 0);
  const startOfLastWeek = new Date(startOfWeek);
  startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);
  const thisWeek = { title: translate('web.wallet_history_modal.this_week_text'), data: [] };
  const lastWeek = { title: translate('web.wallet_history_modal.last_week_text'), data: [] };
  const earlier = { title: translate('web.wallet_history_modal.earlier_text'), data: [] };
  (credits || []).forEach(c => {
    if (!c || !c.actual_reward || c.actual_reward.amount === 0) return;
    const d = new Date(c.date_created);
    if (d >= startOfWeek) thisWeek.data.push(c);
    else if (d >= startOfLastWeek) lastWeek.data.push(c);
    else earlier.data.push(c);
  });
  return [earlier, lastWeek, thisWeek].filter(g => g.data.length > 0);
};

const HistoryRow = ({ item, type, currency, now }) => {
  if (type === 'cash') {
    return (
      <CashPayoutSummaryLine amount={item.amount} currency={item.currency || currency}
        dateCreated={item.date_created} now={now} />
    );
  }
  return <CreditSummaryLine credit={item} now={now} />;
};

const WalletHistoryModal = ({ type, userName, currency, onClose, onBack, credits }) => {
  const { translate, locale } = useTokenTranslate();
  const isCreditMode = type === 'credit' && credits != null;
  const {
    sections: cashSections, loading, hasFetched, nextCursor, errorMessage, fetchHistory, loadMore, cancel
  } = useWalletHistory();
  useEffect(() => cancel, [cancel]);
  const creditSections = useMemo(
    () => isCreditMode ? buildCreditSections(credits, translate) : [],
    [isCreditMode, credits, translate]
  );
  const sections = isCreditMode ? creditSections : cashSections;
  const now = useMemo(() => new Date(), []);
  const modalRef = useRef(null);
  const bodyRef = useRef(null);
  const [sentinelEl, setSentinelEl] = useState(null);
  const loadMoreRef = useRef(loadMore);
  useEffect(() => { loadMoreRef.current = loadMore; }, [loadMore]);
  const loadingRef = useRef(loading);
  useEffect(() => { loadingRef.current = loading; }, [loading]);

  useEffect(() => {
    if (!isCreditMode) fetchHistory();
  }, [isCreditMode, fetchHistory]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (isCreditMode || !sentinelEl || !bodyRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting && !loadingRef.current) loadMoreRef.current(); },
      { root: bodyRef.current, threshold: 0.1 }
    );
    observer.observe(sentinelEl);
    return () => observer.disconnect();
  }, [isCreditMode, sentinelEl]);

  useEffect(() => {
    const trigger = document.activeElement;
    const modal = modalRef.current;
    if (modal) {
      const first = modal.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      first?.focus();
    }
    return () => { trigger?.focus(); };
  }, []);

  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;
    const handleTab = (e) => {
      if (e.key !== 'Tab') return;
      const focusable = Array.from(modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    modal.addEventListener('keydown', handleTab);
    return () => modal.removeEventListener('keydown', handleTab);
  }, []);

  const handleStopPropagation = useCallback((e) => e.stopPropagation(), []);

  const title = type === 'cash'
    ? translate('web.wallet_history_modal.cash_title')
    : locale.startsWith('ar')
      ? `${userName} ${translate('web.pipp_credit_content.credits_text')}`
      : `${userName}'s ${translate('web.pipp_credit_content.credits_text')}`;

  return (
    <Overlay onClick={onClose}>
      <Modal ref={modalRef} role='dialog' aria-modal='true' aria-labelledby='wallet-history-title'
        onClick={handleStopPropagation}>
        <Header>
          <BackButton onClick={onBack} aria-label='Back'>
            <ChevronLeft size={20} />
          </BackButton>
          <Title id='wallet-history-title'>{title}</Title>
          <CloseButton onClick={onClose} aria-label='Close'>
            <X size={18} />
          </CloseButton>
        </Header>

        <Body ref={bodyRef}>
          {!isCreditMode && errorMessage && (
            <EmptyState>{errorMessage}</EmptyState>
          )}

          {!isCreditMode && loading && sections.length === 0 && (
            <LoadingState>
              {SKELETON_ITEMS.map(({ key, width }) => (
                <SkeletonRow key={key} skWidth={width} />
              ))}
            </LoadingState>
          )}

          {!loading && sections.length === 0 && (isCreditMode || hasFetched) && !errorMessage && (
            <EmptyState>{translate('web.wallet_history_modal.no_history_text')}</EmptyState>
          )}

          {sections.map(section => (
            <div key={section.title}>
              <SectionHeader>
                <SectionTitle>
                  {isCreditMode ? section.title : translate(CASH_SECTION_TOKENS[section.title])}
                </SectionTitle>
              </SectionHeader>
              {section.data.map(item => {
                const rowKey = item.id;
                return <HistoryRow key={rowKey} item={item} type={type} currency={currency} now={now} />;
              })}
            </div>
          ))}

          {!isCreditMode && nextCursor && <LoadMoreTrigger ref={setSentinelEl} />}
          {(isCreditMode || (!loading && !nextCursor)) && sections.length > 0 && (
            <EndOfList>{translate('web.wallet_history_modal.end_of_list_text')}</EndOfList>
          )}
        </Body>
      </Modal>
    </Overlay>
  );
};

export default WalletHistoryModal;
